import {
  Box,
  styled,
  useTheme,
  ListItemIcon,
  ListItemText
} from '@material-ui/core';
import { useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { StyledMenuV2 } from '@setvi/shared/components/sdropdown-menu';

import { useDialog } from '@setvi/shared/providers';
import { SButton } from '@setvi/shared/components';
import { Product } from '@setvi/shared/interfaces';
import {
  AIGradientIcon,
  ExchangeIcon,
  EmailIconV2,
  AIIcon
} from '@setvi/shared/components/sicons';
import { TextColor } from '@setvi/shared/enums';
import { SButtonProps } from '@setvi/shared/components/sbutton';
import { StyledMenuItem } from '@setvi/shared/components/intelligence/styled';

import { useStyles } from './styles';
import {
  RecommendationPurposeId,
  RecommendationRequest
} from './dialogs/recommend/components/request';
import { CompareDialogContent } from './dialogs';

const StyledButton = styled(SButton)(({ theme }) => ({
  borderRadius: 120,
  padding: theme.spacing(2, 3)
}));

interface Options extends Partial<SButtonProps> {
  tooltipTitle?: string;
  subOptions?: Options[];
  label: string;
}

export interface SelectedOptionsProps<T> {
  type?: 'product' | 'resource' | 'quote';
  selectedProducts: T[];
  buttonLabel?: string;
  handleSelected?: () => void;
  handleClose: () => void;
  /** Shows a spinner on (and disables) the Cancel button while true. */
  closeLoading?: boolean;
  handleEmail?: () => void;
  /** Shows a spinner on (and disables) the Email button while true. */
  emailLoading?: boolean;
  options?: Options[];
  // Disables the Email action (e.g. when the selection contains a non-shareable
  // resource) and shows emailDisabledTooltip instead of the default tooltip.
  emailDisabled?: boolean;
  emailDisabledTooltip?: string;
}

export const SelectedOptions = <T,>({
  type = 'product',
  selectedProducts = [],
  buttonLabel,
  options,
  handleSelected,
  handleEmail,
  handleClose,
  emailDisabled = false,
  emailDisabledTooltip,
  emailLoading = false,
  closeLoading
}: SelectedOptionsProps<T>) => {
  const flags = useFlags();
  const { openDialog } = useDialog();
  const classes = useStyles();
  const { palette } = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClickAnchor = (event: { currentTarget: any }) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseAnchor = () => {
    setAnchorEl(null);
  };

  const count = selectedProducts.length;
  const disableRecommendAndCompare = count < 2 || count > 4;
  const typeLabel = {
    product: 'Product',
    resource: 'Resource',
    quote: 'Quote'
  }[type];

  const disabledTooltipMessage = (val: string) =>
    `To ${val} products, please select between 2 and 4 products`;

  return (
    <>
      <Box className={classes.container}>
        <Box className={classes.wrapper}>
          <Box className={classes.count}>{count}</Box>

          {handleSelected ? (
            <StyledButton
              variant="text"
              style={{ textDecoration: 'underline' }}
              onClick={handleSelected}>
              Selected
            </StyledButton>
          ) : (
            <span className={classes.button}>Selected</span>
          )}
        </Box>

        {handleEmail && (
          <Box className={classes.wrapper}>
            <StyledButton
              tooltipTitle={
                emailDisabled && emailDisabledTooltip
                  ? emailDisabledTooltip
                  : `Email ${typeLabel}${count > 1 ? 's' : ''}`
              }
              startIcon={
                <EmailIconV2
                  fill={
                    emailDisabled ? palette.text.disabled : palette.primary.main
                  }
                />
              }
              variant="outlined"
              color="primary"
              loading={emailLoading}
              disabled={emailDisabled || emailLoading}
              onClick={handleEmail}>
              {buttonLabel}
            </StyledButton>
          </Box>
        )}

        {options?.map(option => (
          <Box key={option?.key} className={classes.wrapper}>
            {option?.subOptions?.length ? (
              <div>
                <StyledButton
                  variant={option?.variant || 'outlined'}
                  color={option?.color || 'primary'}
                  {...option}
                  onClick={handleClickAnchor}>
                  {option?.label}
                </StyledButton>
                <StyledMenuV2
                  id="customized-menu"
                  anchorEl={anchorEl}
                  keepMounted
                  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}>
                  {option?.subOptions?.map(subOption => (
                    <StyledMenuItem
                      style={{
                        borderRadius: 36
                      }}
                      key={subOption?.key}
                      onClick={e => {
                        subOption?.onClick?.(e as any);
                        handleCloseAnchor();
                      }}>
                      <ListItemIcon>{subOption?.startIcon}</ListItemIcon>
                      <ListItemText primary={subOption?.label} />
                    </StyledMenuItem>
                  ))}
                </StyledMenuV2>
              </div>
            ) : (
              <StyledButton
                variant={option?.variant || 'outlined'}
                color={option?.color || 'primary'}
                onClick={option?.onClick}
                {...option}>
                {option?.label}
              </StyledButton>
            )}
          </Box>
        ))}

        {!!flags?.aiCompare && type === 'product' && (
          <Box className={classes.wrapper}>
            <StyledButton
              variant="outlined"
              startIcon={
                <ExchangeIcon
                  fill={
                    disableRecommendAndCompare
                      ? palette.text.disabled
                      : palette.primary.main
                  }
                />
              }
              onClick={() =>
                openDialog(
                  <CompareDialogContent
                    products={selectedProducts as Product[]}
                  />
                )
              }
              tooltipTitle={
                disableRecommendAndCompare
                  ? disabledTooltipMessage('compare')
                  : 'AI can perform a comparasion analysis'
              }
              disabled={disableRecommendAndCompare}>
              Compare
            </StyledButton>
          </Box>
        )}
        {!!flags?.aiRecommend && type === 'product' && (
          <Box className={classes.wrapper}>
            <StyledButton
              variant="outlined"
              startIcon={
                disableRecommendAndCompare ? (
                  <AIIcon fill={palette.text.disabled} />
                ) : (
                  <AIGradientIcon />
                )
              }
              onClick={() =>
                openDialog(
                  <RecommendationRequest
                    products={selectedProducts as Product[]}
                  />,
                  {
                    id: RecommendationPurposeId
                  }
                )
              }
              tooltipTitle={
                disableRecommendAndCompare
                  ? disabledTooltipMessage('recommend')
                  : 'AI can perform a recommendation analysis based on a use case, application or segment'
              }
              disabled={disableRecommendAndCompare}>
              Help me Recommend
            </StyledButton>
          </Box>
        )}

        {handleClose && (
          <Box className={classes.wrapper}>
            <StyledButton
              variant="text"
              style={{ color: TextColor.SlateGrey }}
              loading={closeLoading}
              disabled={closeLoading}
              onClick={handleClose}>
              Cancel
            </StyledButton>
          </Box>
        )}
      </Box>
    </>
  );
};
